import { Dispatch, SetStateAction } from 'react'
import { Button } from 'primereact/button'
import styled, { css } from 'styled-components'
import moment from 'moment'

// local imports
import { IGetLactationRoomTimeTableList } from 'types/LactationType'
import DateUtil from 'utils/DateUtil'

interface SchedulerProps {
  filterDate: Date
  setFilterDate: Dispatch<SetStateAction<Date>>
  tableWeek: { start: Date; end: Date }
  dailyAppointments: any
  setSelectedAppointment: Dispatch<SetStateAction<IGetLactationRoomTimeTableList | undefined>>
  selectedAppointment: IGetLactationRoomTimeTableList | undefined
}

export default function Scheduler({
  filterDate,
  setFilterDate,
  dailyAppointments,
  setSelectedAppointment,
  selectedAppointment,
  tableWeek,
}: SchedulerProps) {
  // variables
  const hours = [...Object.values(dailyAppointments)]
  const selectedDayByFilter = moment(filterDate).format('Do MMMM dddd')
  const weekDays = DateUtil.getWeekDayRange(tableWeek.start, tableWeek.end)
  const selectedTime = moment(selectedAppointment?.reservationStartHour).format('Do MMMM dddd')
  const selectedDay = weekDays.find((wd) => wd === selectedTime)
  const isTimeSelected = (h: IGetLactationRoomTimeTableList) =>
    `${selectedAppointment?.reservationStartHour}-${selectedAppointment?.reservationEndHour}` ===
    `${h.reservationStartHour}-${h.reservationEndHour}`
      ? 'selected'
      : ''

  const isDaySelected = (day: string) => {
    if (!selectedAppointment) return day === selectedDayByFilter
    else return day === selectedDay
  }

  const handleClick = (data: IGetLactationRoomTimeTableList) => {
    setSelectedAppointment(data)
    const date = new Date(data.reservationStartHour)
    setFilterDate(date)
  }

  return (
    <>
      <HeaderWrapper>
        {weekDays.map((day) => (
          <DayHeader key={day} selected={isDaySelected(day)}>
            <span>{day}</span>
          </DayHeader>
        ))}
      </HeaderWrapper>

      <GridWrapper>
        <GridContainer>
          {hours?.map((hour: any, index) => (
            <GridItem key={index}>
              {hour?.map((h: IGetLactationRoomTimeTableList) => (
                <TimeButton
                  disabled={!h.isSelectable}
                  id={`${h.reservationStartHour}-${h.reservationEndHour}`}
                  onClick={() => handleClick(h)}
                  className={isTimeSelected(h)}
                  key={`${h.reservationStartHour}-${h.reservationEndHour}`}>
                  <span>
                    {h.reservationStartHourStr} - {h.reservationEndHourStr}
                  </span>
                </TimeButton>
              ))}
            </GridItem>
          ))}
        </GridContainer>
      </GridWrapper>
    </>
  )
}

const HeaderWrapper = styled.div`
  display: flex;
  width: calc(100% - 10px);
  border: 1px solid #ebebeb;
  border-radius: 4px;
`
const DayHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-grow: 1;
  height: 40px;
  font-size: 14px;
  line-height: 17px;
  font-weight: 700;
  color: #363636;

  ${({ selected }: { selected: boolean }) =>
    selected &&
    css`
      background: #e3f5fb;
      border-bottom: 1px solid #ebebeb;
    `}
`

const GridWrapper = styled.div`
  overflow-y: auto;
  max-height: calc(100% - 220px);
  width: calc(100% - 6px) !important;

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: none !important;
    background-color: #d3d3d3 !important;
  }

  &::-webkit-scrollbar {
    height: 6px;
    width: 6px !important;
    background-color: #d3d3d3;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #7c7c7c;
  }
`

const GridContainer = styled.div`
  display: flex;
  padding: 0px;
`

const GridItem = styled.div`
  display: flex;
  padding: 0 10px;
  width: 100%;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  border-right: 1px solid #ebebeb;

  .selected {
    background: #45bfe5 !important;
    color: #fff !important;
  }

  &:last-child {
    border-right: none;
  }
`

const TimeButton = styled(Button)`
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed !important;
      border: none !important;
    `}

  cursor: pointer;
  padding: 0;
  margin: 5px 0;
  width: 100%;
  height: 30px;
  background: #ffffff;
  border: 0.5px solid #cccccc;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #363636;

  &:disabled {
    opacity: 0.4 !important;
  }

  &:hover {
    background: #45bfe5 !important;
    color: #fff !important;
  }
`
